// @flow
import * as React from 'react';
import {
  Button,
  Spin,
  Input,
  Popconfirm,
  Modal,
  Typography,
} from 'antd';
import FilesManager from '../filesManager/FilesManager';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
  createMedicalProfileResourceItem,
  updateMedicalProfileResourceItem,
  deleteMedicalProfileResourceItem,
} from '../../store/slices/medicalProfileSlice';
import { useFilesManagerHandler } from '../../hooks/useFilesManagerHandler';
import { useInputState } from '../../hooks/useInputState';
import { removeKeysWithUndefinedValue } from '../../utils';
import message from '../../utils/message';
import type {
  MPFamilyHistory,
  MedicalProfileResource,
} from '../../config/types-mp';
import EditRow from '../editRow/EditRow';
import { unwrapResult } from '@reduxjs/toolkit';


type Props = {
  consultationId?: number,
  dataItem?: MPFamilyHistory,
  readOnly?: boolean,
  title?: string,
  onCloseModal: Function,
  visible: boolean
};

const LoadingIndicator = (props) => (
  <div style={props.style}>
    <Spin size={'small'} />
  </div>
);

const { TextArea } = Input;

const MPEditFamilyHistoryItem = (props: Props) => {
  if (props?.title == null) {
    console.warn(
      'MPEditFamilyHistoryItemScreen expects "title" to be passed as navigation parameters',
    );
  }
  const { t } = useTranslation();
  const resource: MedicalProfileResource = 'family_history';
  const dataItem: MPFamilyHistory = props?.dataItem || {};
  const readOnly: boolean = Boolean(props?.readOnly);
  const consultation_id: ?number = props?.consultationId;
  const patient_id = useSelector((state) => state.profile.user.id);

  const dispatch = useDispatch();

  const notesInput = React.useRef(null);
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [savingErrorMeta, setSavingErrorMeta] = React.useState({});
  const {
    files,
    onFilesUpdated,
    continueWithFilesUploadCaution,
  } = useFilesManagerHandler(dataItem.files);

  // HISTORY ITEM PROPERTY related --- text props
  const notesState = useInputState(dataItem.notes);

  const onSave = React.useCallback(async () => {
    const data = removeKeysWithUndefinedValue({
      notes: notesState.value === '' ? null : notesState.value,
      files,
    });

    if (Object.keys(data).length === 0) {
      // ok, there is no change..
      // just go back
      props.onCloseModal(false);
    } else {
      try {
        setSaving(true);
        setSavingErrorMeta({});
        if (dataItem.id) {
          await dispatch(
            updateMedicalProfileResourceItem({
              patient_id,
              consultation_id,
              itemId: dataItem.id,
              data,
              resource,
            }),
          ).then(unwrapResult);
        } else {
          await dispatch(
            createMedicalProfileResourceItem({
              patient_id,
              consultation_id,
              data,
              resource,
            }),
          ).then(unwrapResult);
        }
        props.onCloseModal(false);
      } catch (error) {
        setSaving(false);
        setSavingErrorMeta(error.meta);
        message.error(error.message);
      }
    }
  }, [
    notesState,
    files,
    patient_id,
    consultation_id,
    dataItem,
    resource,
    t,
    dispatch,
  ]);

  const trySave = React.useCallback(() => {
    continueWithFilesUploadCaution()
      .then(onSave)
      .catch((_) => console.log('user cancelled'));
  }, [continueWithFilesUploadCaution, onSave]);

  const onDelete = () => {
    try {
      setDeleting(true);
      if (dataItem.id != null) {
        dispatch(
          deleteMedicalProfileResourceItem({
            patient_id,
            consultation_id,
            itemId: dataItem.id,
            resource,
          }),
        ).then(unwrapResult)
          .then(() => {
            props.onCloseModal(false);
          });
      }
    } catch (error) {
      setDeleting(false);
      message.error(error.message);
    }
  };

  return (
    <Modal
      onCancel={() => props.onCloseModal(!props.visible)}
      closable={false}
      maskClosable={false}
      centered={true}
      title={t('Family history')}
      className={'profile-modal'}
      bodyStyle={styles.modalBackground}
      cancelText={t('Cancel')}
      onOk={trySave}
      confirmLoading={saving}
      visible={props.visible}
    >
      <EditRow text={t('Notes')} value={notesState.value}>
        {!readOnly ? (
          <TextArea
            ref={notesInput}
            rows={4}
            disabled={readOnly}
            style={styles.input}
            status={savingErrorMeta?.notes != null ? 'danger' : null}
            caption={
              savingErrorMeta.notes != null
                ? t('This field is required')
                : null
            }
            {...notesState}
          />
        ) : null}
      </EditRow>
      <EditRow text={t('Attachments')}>
        <FilesManager
          autoUploadNewFiles={true}
          editable={!readOnly}
          files={dataItem.files}
          onFilesUpdated={onFilesUpdated}
        />
      </EditRow>
      {dataItem.id != null && !readOnly ? (
        <Popconfirm
          placement="bottom"
          title={t('Are you sure you want to delete this entry?')}
          onConfirm={onDelete}
          okText={t('Yes')}
          cancelText={t('No')}>
          <Button
            loading={deleting}
            size={'middle'}
            style={styles.deleteButton}
          >
            {t('Delete entry')}
          </Button>
        </Popconfirm>
      ) : null}
    </Modal>
  );
};

const styles = {
  container: {
    flex: 1,
    paddingLeft: 16,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  titleContainer: {
    flex: 1,
  },
  title: {
    textAlign: 'center',
  },
  input: {
    backgroundColor: '#ffffff',
  },
  inputText: {},
  notesText: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  checkbox: {
    alignSelf: 'flex-start',
  },
  deleteButton: {
    alignSelf: 'center',
    marginVertical: 24,
  },
  modalBackground: {
    backgroundColor: 'rgba(72, 72, 72, 0.1)'
  }
};

export default MPEditFamilyHistoryItem;
